import React, { useState, useEffect } from 'react';
import useApi from '../../hooks/useApi';
import haxios from '../../lib/haxios';
import {
    PlusIcon,
    TrashIcon
} from '@heroicons/react/24/outline';

export default function IncidentsTab({ vehicleId }) {
    const { data: incidentsResponse, refetch, isLoading } = useApi({ url: `/visiondashboard/salesassist/vehicles/${vehicleId}/incidents`, initial: { data: [] } });
    const { data: driversResponse } = useApi({ url: `/visiondashboard/salesassist/drivers`, initial: { data: [] } });

    const [description, setDescription] = useState('');
    const [insuranceClaimNumber, setInsuranceClaimNumber] = useState('');
    const [selectedDriver, setSelectedDriver] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const incidents = incidentsResponse?.data || [];
    const drivers = driversResponse?.data || [];

    const handleSubmit = async () => {
        if (!description || !selectedDriver) {
            alert("Description and Driver must be selected");
            return;
        }

        setIsSubmitting(true);

        try {
            await haxios.post(`/visiondashboard/salesassist/vehicles/${vehicleId}/incidents`, {
                driver_id: selectedDriver, // staff_id (UUID)
                description,
                insurance_claim_number: insuranceClaimNumber || null
            });

            setDescription('');
            setInsuranceClaimNumber('');
            setSelectedDriver('');
            refetch(); // Refresh incidents list
        } catch (error) {
            console.error('Error adding incident:', error);
            alert('Failed to add incident');
        }

        setIsSubmitting(false);
    };

    const handleDelete = async (incidentId) => {
        // Use window.confirm instead of direct confirm to satisfy ESLint
        if (!window.confirm('Are you sure you want to delete this incident?')) {
            return;
        }

        setIsDeleting(true);

        try {
            await haxios.delete(`/visiondashboard/salesassist/vehicles/${vehicleId}/incidents/${incidentId}`);
            refetch(); // Refresh incidents list after deletion
        } catch (error) {
            console.error('Error deleting incident:', error);
            alert('Failed to delete incident');
        }

        setIsDeleting(false);
    };

    return (
        <div className="p-4">
            <h2 className="text-lg font-semibold mb-4">Vehicle Incidents</h2>

            {/* Add Incident Form */}
            <div className="border p-4 rounded-lg bg-gray-100 mb-6">
                <h3 className="text-md font-medium mb-3">Log a New Incident</h3>

                <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700">Description</label>
                    <textarea
                        className="mt-1 block w-full border rounded-md p-2"
                        value={description}
                        onChange={(e) => setDescription(e.target.value)}
                    />
                </div>

                <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700">Insurance Claim Number (if any)</label>
                    <input
                        type="text"
                        className="mt-1 block w-full border rounded-md p-2"
                        value={insuranceClaimNumber}
                        onChange={(e) => setInsuranceClaimNumber(e.target.value)}
                    />
                </div>

                <div className="mb-3">
                    <label className="block text-sm font-medium text-gray-700">Assign Driver</label>
                    <select
                        className="mt-1 block w-full border rounded-md p-2"
                        value={selectedDriver}
                        onChange={(e) => setSelectedDriver(e.target.value)}
                    >
                        <option value="">Select Driver</option>
                        {drivers.map((driver) => (
                            <option key={driver.staff_id} value={driver.staff_id}>
                                {driver.Name} ({driver.license_number})
                            </option>
                        ))}
                    </select>
                </div>

                <button
                    className="bg-indigo-600 text-white px-4 py-2 rounded-md flex items-center"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                >
                    <PlusIcon className="w-5 h-5 mr-2" />
                    {isSubmitting ? 'Adding...' : 'Add Incident'}
                </button>
            </div>

            {/* Incident List */}
            {isLoading ? (
                <div className="p-4 text-center">
                    <p className="text-gray-600 mt-2">Loading incidents...</p>
                </div>
            ) : (
                <>
                    <h3 className="text-md font-medium mb-3">Incident History</h3>

                    {incidents.length > 0 ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                            {incidents.map((incident) => (
                                <div key={incident.id} className="bg-white p-4 rounded-lg shadow hover:shadow-md transition-shadow">
                                    <p className="font-medium text-gray-800">{incident.description}</p>
                                    <p className="text-sm text-gray-600 mt-1">
                                        Driver: {incident.driver_name || 'Unknown'}
                                    </p>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Date: {new Date(incident.date).toLocaleDateString()}
                                    </p>
                                    {incident.insurance_claim_number && (
                                        <p className="text-sm text-red-500 mt-1">
                                            Claim: {incident.insurance_claim_number}
                                        </p>
                                    )}

                                    <button
                                        className="mt-2 p-1 rounded-full hover:bg-red-100"
                                        title="Delete Incident"
                                        onClick={() => handleDelete(incident.id)}
                                        disabled={isDeleting}
                                    >
                                        <TrashIcon className="w-5 h-5 text-red-600" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center p-8 bg-gray-50 rounded-lg border border-gray-200">
                            <p className="text-gray-500">No incidents logged yet.</p>
                        </div>
                    )}
                </>
            )}
        </div>
    );
}